import actionTypes from 'constants/actionTypes'
import { maxRetries } from 'constants/apiConfig'
import { selectLoggedIn } from 'selectors/auth'
import selectOffline from 'selectors/offline'
import type { ActionShape, DispatchFn, ThunkFn } from 'types/adapters'


const retryIntervalTime = 5000
let retryInterval: ReturnType<typeof setInterval> | null = null

type PendingState = {
  pendingActions?: {
    actions?: RetryablePendingAction[]
    retrying?: boolean
  }
}

type PendingActionMeta = {
  pending?: boolean
  previousAction?: {
    meta?: {
      retries?: number
    }
  }
}

type PendingActionResult = {
  meta?: PendingActionMeta
  error?: unknown
}

type RetryablePendingAction = ActionShape & {
  id?: string
}

type Dispatch = DispatchFn
type GetState = () => PendingState
type PendingThunk = ThunkFn<ActionShape>

export const enable = () => {
  return {
    type: actionTypes.PENDING.RETRY.ENABLE
  }
}

export const disable = () => {
  return {
    type: actionTypes.PENDING.RETRY.DISABLE
  }
}

export const retry = () => {
  return ((dispatch: Dispatch, getState: GetState) => {
    const state = getState()
    const { actions = [], retrying = false } = state.pendingActions ?? {}
    const { loggedIn } = selectLoggedIn(state)
    const { isOffline } = selectOffline(state)

    if (isOffline || retrying || actions.length === 0 || !loggedIn) {
      return
    }

    dispatch({ type: actionTypes.PENDING.RETRY.START })

    let p = Promise.resolve([] as string[])
    actions.forEach((a: RetryablePendingAction) => {
      p = p.then((ids) => {
        return Promise.resolve()
          .then(() => dispatch(a))
          .then((action: unknown) => {
            const actionResult = (action ?? null) as PendingActionResult | null
            if (action == null) {
              return ids
            }
            const retries = actionResult?.meta?.previousAction?.meta?.retries ?? 0
            if (actionResult?.meta?.pending || (actionResult?.error && retries < maxRetries)) {
              return ids
            }
            return typeof a.id === 'string' ? ids.concat(a.id) : ids
          })
      })
    })

    return p.then((ids) => {
      return dispatch({
        type: actionTypes.PENDING.RETRY.END,
        payload: { ids }
      })
    })
  }) as PendingThunk
}

export const setRetryInterval = () => {
  return ((dispatch: Dispatch) => {
    if (retryInterval !== null) {
      clearInterval(retryInterval)
    }
    retryInterval = setInterval(() => {
      dispatch(retry())
    }, retryIntervalTime)
  }) as PendingThunk
}
